import React, { useMemo } from 'react';
import { formatNumEU } from '../dummyData';
import { HiddenValue } from '../ValueHideContext';
import { SubscriptionSummary } from '../../../../types/subscription';
import AssetIcon from '../../../../components/AssetIcon';

// ── Bar Row ───────────────────────────────────────────────────────────────────

interface BarRowProps {
  label: string;
  quoteVal: number;
  assetVal: number;
  icon?: string;
  isCluster?: boolean;
}

const BarRow: React.FC<BarRowProps> = ({
  label,
  quoteVal,
  assetVal,
  icon,
  isCluster,
}) => {
  const total = quoteVal + assetVal;
  // Each side is a half. The bar fills proportionally to the side based on its share of own total.
  const quoteWidth = total > 0 ? (quoteVal / total) * 50 : 0;
  const assetWidth = total > 0 ? (assetVal / total) * 50 : 0;

  return (
    <div
      className="aq-row"
      style={
        isCluster
          ? {
              borderBottom: '1px solid rgba(128,128,128,0.2)',
              paddingBottom: 8,
              marginBottom: 4,
            }
          : undefined
      }
    >
      <div className="aq-bot">
        {icon && (
          <AssetIcon
            baseAsset={icon}
            width={16}
            height={16}
            style={{ marginBottom: 0 }}
          />
        )}
        <span
          className="aq-bot-name"
          style={isCluster ? { fontWeight: 700 } : undefined}
        >
          {label}
        </span>
      </div>

      <div className="aq-bar-cell">
        <div className="aq-center-line" />

        {quoteVal > 0 && (
          <div
            className="aq-bar aq-bar--quote"
            style={{ right: '50%', width: `${quoteWidth}%` }}
          />
        )}

        {assetVal > 0 && (
          <div
            className="aq-bar aq-bar--asset"
            style={{ left: '50%', width: `${assetWidth}%` }}
          />
        )}

        <span className="aq-bar-label aq-bar-label--left">
          <HiddenValue width={40}>
            {quoteVal > 0 ? `$${formatNumEU(quoteVal)}` : '$0'}
          </HiddenValue>
        </span>
        <span className="aq-bar-label aq-bar-label--right">
          <HiddenValue width={40}>
            {assetVal > 0 ? `$${formatNumEU(assetVal)}` : '$0'}
          </HiddenValue>
        </span>
      </div>
    </div>
  );
};

// ── AssetQuoteBalance ─────────────────────────────────────────────────────────

interface AssetQuoteBalanceProps {
  subscriptions: SubscriptionSummary[];
}

const AssetQuoteBalance: React.FC<AssetQuoteBalanceProps> = ({
  subscriptions,
}) => {
  const sortedSubs = useMemo(
    () =>
      [...subscriptions].sort(
        (a, b) => b.totalBalanceInStable - a.totalBalanceInStable,
      ),
    [subscriptions],
  );

  const clusterStable = useMemo(
    () => subscriptions.reduce((acc, s) => acc + s.stableBalance, 0),
    [subscriptions],
  );
  const clusterTotal = useMemo(
    () => subscriptions.reduce((acc, s) => acc + s.totalBalanceInStable, 0),
    [subscriptions],
  );
  const clusterAsset = Math.max(0, clusterTotal - clusterStable);

  return (
    <div className="bottom-card">
      <div className="aq-title-row">
        <span className="bottom-card-title" style={{ marginBottom: 0 }}>
          Balance Distribution
        </span>
        <div className="aq-legend-row">
          <span className="aq-legend-item aq-legend-item--quote">In Quote</span>
          <span className="aq-legend-item aq-legend-item--asset">In Asset</span>
        </div>
      </div>

      <div className="aq-header">
        <div>Bot</div>
        <div className="aq-header-cols">
          <span>Quote (Stables)</span>
          <span>Asset</span>
        </div>
      </div>

      <div className="aq-rows no-scrollbar">
        <BarRow
          label="Cluster Total"
          quoteVal={clusterStable}
          assetVal={clusterAsset}
          isCluster
        />
        {sortedSubs.map((sub) => (
          <BarRow
            key={sub.id}
            label={sub.baseAsset}
            quoteVal={sub.stableBalance}
            assetVal={Math.max(0, sub.assetBalanceInStable)}
            icon={sub.baseAsset}
          />
        ))}
      </div>
    </div>
  );
};

export default AssetQuoteBalance;
